import { amountToChainUnits, chainUnitsToAmount } from "@/utils/math";

import { quoteTransferFee, type TransferSpeed } from "./fees";
import type { IrisRouteFees } from "./iris";

// Circle quotes Fast at ~8 s (~20 s from Ethereum); testnet Base → Aptos Fast took 11 s.
const FAST_ETA_SECONDS: Record<string, number> = { Ethereum: 30, Solana: 15 };
const DEFAULT_FAST_ETA_SECONDS = 15;
// Testnet Base → Aptos Standard attested in ~21 min. Solana is Circle's published ~25 s. Circle
// lists Sui at ~0.5 s, like Aptos, which took ~10 min on testnet; not yet measured.
const STANDARD_ETA_SECONDS: Record<string, number> = { Solana: 30, Sui: 30 };
const DEFAULT_STANDARD_ETA_SECONDS = 20 * 60;

/** How long a v2 deposit from `chain` takes to attest at `speed`. */
export function v2DepositEtaSeconds(chain: string, speed: TransferSpeed): number {
  return speed === "fast"
    ? (FAST_ETA_SECONDS[chain] ?? DEFAULT_FAST_ETA_SECONDS)
    : (STANDARD_ETA_SECONDS[chain] ?? DEFAULT_STANDARD_ETA_SECONDS);
}

/** The speed a v2 deposit from `chain` burns at: Sui has no Fast Transfer as a source. */
export function v2DepositSpeed(chain: string, requested: TransferSpeed): TransferSpeed {
  return chain === "Sui" ? "standard" : requested;
}

export type FastUnavailableReason =
  /** A fee or allowance lookup failed, or the fee would leave nothing to receive. */
  | "unavailable"
  /** Circle has no Fast tier on the route. */
  | "not_offered"
  /** The amount is above Circle's remaining global Fast allowance. */
  | "above_allowance";

export interface SpeedQuote {
  etaSeconds: number;
  /** Circle's fee rate in basis points, known before the amount; null when it has none to quote. */
  feeBps: number | null;
  /** Expected fee in USDC; null without an amount or a fee rate. */
  fee: number | null;
  /** What lands on Aptos after the fee; null whenever `fee` is. */
  receive: number | null;
}

export interface DepositSpeedQuote {
  fast: SpeedQuote;
  standard: SpeedQuote;
  /** Why Fast can't be offered, or null when it can. */
  fastUnavailable: FastUnavailableReason | null;
}

function quoteSpeed(
  etaSeconds: number,
  amount: number | undefined,
  bps: number | null,
): SpeedQuote | null {
  if (!amount || amount <= 0 || bps === null) {
    return { etaSeconds, feeBps: bps, fee: null, receive: null };
  }
  const units = BigInt(amountToChainUnits(amount));
  const quote = quoteTransferFee(units, bps);
  if (!quote) return null;
  return {
    etaSeconds,
    feeBps: bps,
    fee: chainUnitsToAmount(Number(quote.expectedFee)),
    receive: chainUnitsToAmount(Number(units - quote.expectedFee)),
  };
}

/**
 * Both speeds of a v2 deposit for the picker. `fees` and `fastAllowance` are null when their
 * lookup failed; either failing turns Fast off, since we never offer a paid path we can't quote.
 */
export function quoteDepositSpeeds({
  chain,
  amount,
  fees,
  fastAllowance,
}: {
  chain: string;
  amount: number | undefined;
  fees: IrisRouteFees | null;
  /** Whole USDC. */
  fastAllowance: number | null;
}): DepositSpeedQuote {
  const fastEta = v2DepositEtaSeconds(chain, "fast");
  const standardEta = v2DepositEtaSeconds(chain, "standard");
  const fast = quoteSpeed(fastEta, amount, fees?.fastBps ?? null);
  const standard = quoteSpeed(standardEta, amount, fees?.standardBps ?? null) ?? {
    etaSeconds: standardEta,
    feeBps: null,
    fee: null,
    receive: null,
  };

  let fastUnavailable: FastUnavailableReason | null = null;
  if (!fees || fastAllowance === null || !fast) fastUnavailable = "unavailable";
  else if (fees.fastBps === null || v2DepositSpeed(chain, "fast") !== "fast") {
    fastUnavailable = "not_offered";
  } else if (amount && amount > fastAllowance) fastUnavailable = "above_allowance";

  return {
    fast: fast ?? { etaSeconds: fastEta, feeBps: null, fee: null, receive: null },
    standard,
    fastUnavailable,
  };
}

/** The speed a deposit actually burns at: Fast only while it can be offered. */
export function effectiveSpeed(picked: TransferSpeed, quote: DepositSpeedQuote): TransferSpeed {
  return picked === "fast" && quote.fastUnavailable === null ? "fast" : "standard";
}
